import {useCallback, useContext, useEffect, useRef} from 'react';
import {Subject, firstValueFrom, filter, timeout, of, map} from 'rxjs';
import {v4 as uuid} from 'uuid';
import {
  ClientResponse,
  REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
  RequestAction,
} from '@stripe-internal/conversations-client';
import {
  MessageToParentType,
  ParentMessageBusContext,
} from './Providers/ParentMessageBusProvider';
import {
  MessageFromParent,
  RequestActionResponseMessage,
} from './Providers/ParentMessageBusProvider/MessageFromParent';
import {useClientObservability} from './Providers/ClientProvider/useClientObservability';

export function useRequestAction() {
  const {post, addListener, removeListener} = useContext(
    ParentMessageBusContext,
  );

  const {errors} = useClientObservability();

  const subject = useRef<Subject<RequestActionResponseMessage>>(new Subject());
  const parentMessagesObs = subject.current.asObservable();

  useEffect(() => {
    // this effect subscribes to message events, emitting response events to
    // our internal message observable
    const handler = (message: MessageFromParent) => {
      if (message.type === 'request-action-response') {
        subject.current.next(message);
      }
    };

    addListener(handler);

    return () => removeListener(handler);
  }, [addListener, removeListener]);

  const postRequestAction = useCallback(
    (action: RequestAction) => {
      // the nonce will associate a response with our request
      const nonce = uuid();

      const {
        waitForResponse,
        responseTimeout,
        action: requestedAction,
      } = action;

      let response: Promise<ClientResponse>;

      if (waitForResponse) {
        const timeoutResponse = {
          code: 400,
          error: `Timeout waiting for request-action response for ${action.action.name}`,
        };

        response = firstValueFrom(
          parentMessagesObs.pipe(
            // find the response message that matches our request nonce
            filter((m) => m.nonce === nonce),
            // map the response data a successful ClientResponse
            map((m) => {
              return {code: 200, data: m.data};
            }),
            // handle any timeouts
            timeout({
              // use a specified or default timeout
              each: responseTimeout ?? REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
              with: () => {
                // report timeouts
                errors.reportError(timeoutResponse.error);
                // return our defined timeout ClientResponse
                return of(timeoutResponse);
              },
            }),
          ),
        );
      } else {
        // if not waiting for a response, resolve immediately
        response = Promise.resolve({code: 200});
      }

      post({
        type: MessageToParentType.RequestAction,
        data: requestedAction,
        nonce,
      });

      return response;
    },
    [post, parentMessagesObs, errors],
  );

  return postRequestAction;
}
